/**
 * O que faz: permite digitar imediatamente, sugerir serviços existentes e usar um novo título rapidamente.
 * Onde usa: modal de novo agendamento e edição de agendamento na Agenda.
 * Conexões: react-hook-form, useClinicServices e cálculo de preço/duração no componente pai.
 * Tabelas: clinica_servicos, por meio do hook usado no componente pai.
 * Data: 22/07/2026
 */
import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Check, Loader2, Plus, Search } from 'lucide-react';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ClinicaServico } from '@/hooks/useClinicServices';
import { AgendamentoFormData } from './types';

interface ServicoSelectorProps {
  form: UseFormReturn<AgendamentoFormData>;
  servicos: ClinicaServico[];
  loadingServices: boolean;
  modoServico: 'selecionar' | 'manual';
  setModoServico: (modo: 'selecionar' | 'manual') => void;
  servicoSelecionadoId: string | null;
  setServicoSelecionadoId: (id: string | null) => void;
  onServicoSelecionado?: (servico: ClinicaServico | null) => void;
}

export const ServicoSelector = ({
  form,
  servicos,
  loadingServices,
  setModoServico,
  servicoSelecionadoId,
  setServicoSelecionadoId,
  onServicoSelecionado,
}: ServicoSelectorProps) => {
  const [servicoComboboxOpen, setServicoComboboxOpen] = useState(false);
  const servicosSeguro = Array.isArray(servicos) ? servicos : [];
  const tituloDigitado = form.watch('titulo') ?? '';
  const buscaNormalizada = tituloDigitado.trim().toLocaleLowerCase('pt-BR');

  // Exibe somente as primeiras correspondências para o menu abrir sem travamentos.
  const servicosFiltrados = (buscaNormalizada
    ? servicosSeguro.filter(servico =>
        servico.nome_servico.toLocaleLowerCase('pt-BR').includes(buscaNormalizada)
      )
    : servicosSeguro
  ).slice(0, 10);

  const existeCorrespondenciaExata = servicosSeguro.some(
    servico => servico.nome_servico.trim().toLocaleLowerCase('pt-BR') === buscaNormalizada,
  );

  return (
    <FormField
      control={form.control}
      name="titulo"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Título/Serviço *</FormLabel>
          <Popover open={servicoComboboxOpen} onOpenChange={setServicoComboboxOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    role="combobox"
                    aria-expanded={servicoComboboxOpen}
                    placeholder="Digite ou selecione um serviço..."
                    value={field.value ?? ''}
                    onFocus={() => setServicoComboboxOpen(true)}
                    onChange={(event) => {
                      field.onChange(event.target.value);
                      setModoServico('manual');
                      setServicoSelecionadoId(null);
                      onServicoSelecionado?.(null);
                      setServicoComboboxOpen(true);
                    }}
                  />
                </div>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-[--radix-popover-trigger-width] p-0"
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              <Command shouldFilter={false}>
                <CommandList>
                  {loadingServices && (
                    <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Buscando serviços em segundo plano...
                    </div>
                  )}
                  {servicosFiltrados.length > 0 && (
                    <CommandGroup heading="Serviços encontrados">
                      {servicosFiltrados.map(servico => (
                        <CommandItem
                          key={servico.id}
                          value={servico.id}
                          onSelect={() => {
                            field.onChange(servico.nome_servico);
                            setModoServico('selecionar');
                            setServicoSelecionadoId(servico.id);
                            onServicoSelecionado?.(servico);
                            setServicoComboboxOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              servicoSelecionadoId === servico.id ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          {servico.nome_servico}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {buscaNormalizada.length >= 2 && !existeCorrespondenciaExata && (
                    <CommandGroup heading="Novo serviço neste agendamento">
                      <CommandItem
                        value={`novo:${tituloDigitado}`}
                        onSelect={() => {
                          field.onChange(tituloDigitado.trim());
                          setModoServico('manual');
                          setServicoSelecionadoId(null);
                          setServicoComboboxOpen(false);
                        }}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Usar “{tituloDigitado.trim()}”
                      </CommandItem>
                    </CommandGroup>
                  )}
                  {!loadingServices && !buscaNormalizada && servicosSeguro.length === 0 && (
                    <div className="p-3 text-sm text-muted-foreground">
                      Digite o serviço para continuar sem bloquear o agendamento.
                    </div>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
